'use client';

import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { usePathname, useRouter } from 'next/navigation';
import React from 'react';

import delay from 'lib/delay';
import { Button } from 'toolkit/chakra/button';
import { IconButton } from 'toolkit/chakra/icon-button';
import AddressEntity from 'ui/shared/entities/address/AddressEntity';
import IconSvg from 'ui/shared/IconSvg';

import UserWalletAutoConnectAlert from '../UserWalletAutoConnectAlert';

// Routes that require a connected wallet — disconnecting while on one of these
// (or a subroute) should send the user back to the landing page.
const WALLET_GATED_ROUTES = [ '/asset' ];

interface Props {
  address: string;
  domain?: string;
  isAutoConnectDisabled?: boolean;
  isReconnecting?: boolean;
  onDisconnect: () => void;
  onOpenWallet: () => void;
}

const UserWalletMenuContent = ({
  isAutoConnectDisabled,
  address,
  domain,
  isReconnecting,
  onDisconnect,
  onOpenWallet,
}: Props) => {
  const router = useRouter();
  const pathname = usePathname();

  const handleOpenWalletClick = React.useCallback(async() => {
    await delay(100);
    onOpenWallet();
  }, [ onOpenWallet ]);

  const handleDisconnectClick = React.useCallback(() => {
    onDisconnect();

    const isOnGatedRoute = WALLET_GATED_ROUTES.some(
      (route) => pathname === route || pathname?.startsWith(`${ route }/`),
    );

    if (isOnGatedRoute) {
      router.push('/');
    }
  }, [ onDisconnect, pathname, router ]);

  return (
    <Box>
      { isAutoConnectDisabled && <UserWalletAutoConnectAlert/> }
      { /* <Text fontSize="sm" fontWeight={600} mb={1}>
        My wallet
      </Text> */ }
      <Text fontSize="sm" mb={ 5 } fontWeight={ 400 } color="text.secondary">
        Your wallet is used to interact with apps and contracts in the explorer.
      </Text>
      <Flex alignItems="center" columnGap={ 2 } justifyContent="space-between">
        <AddressEntity
          address={{ hash: address, ens_domain_name: domain }}
          truncation="dynamic"
          fontSize="sm"
          fontWeight={ 700 }
        />
        { isReconnecting ? (
          <Spinner size="sm" m="2px" flexShrink={ 0 }/>
        ) : (
          <IconButton
            aria-label="Open wallet"
            variant="icon_secondary"
            size="2xs"
            onClick={ handleOpenWalletClick }
          >
            <IconSvg name="gear_slim"/>
          </IconButton>
        ) }
      </Flex>

      <Button
        size="sm"
        width="full"
        variant="surface"
        onClick={ handleDisconnectClick }
        mt={ 6 }
        style={{
          color: '#cc3737',
          borderColor: '#cc3737',
        }}
      >
        Disconnect
      </Button>
    </Box>
  );
};

export default React.memo(UserWalletMenuContent);
